/** @graphql agg **/
import { useCallback } from 'react';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { EDIT_PAGE_V2 } from '@confluence/named-routes/entry-points/routes.ts';

import { UseIsAutoConvertedRecentlyQuery } from '../useIsAutoConvertedRecently/useIsAutoConvertedRecently.ts';

import type {
	useMarkAutoConvertedDateMutation$data,
	useMarkAutoConvertedDateMutation$variables,
} from './__generated__/useMarkAutoConvertedDateMutation.graphql.ts';

type UseMarkAutoConvertedDateArgsType = {
	cloudId: string;
	contentId: string;
};

type UseMarkAutoConvertedDateReturnType = {
	markAutoConvertedDate: () => void;
};

export const useMarkAutoConvertedDate = ({
	cloudId,
	contentId,
}: UseMarkAutoConvertedDateArgsType): UseMarkAutoConvertedDateReturnType => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	// Mutation which "marks" a page by adding the autoConvertedDate content property to the page
	const [markAutoConvertedDateMutation] = useMutationInterop<
		useMarkAutoConvertedDateMutation$data,
		useMarkAutoConvertedDateMutation$variables
	>(
		graphql`
			mutation useMarkAutoConvertedDateMutation(
				$input: ConfluenceCreateAutoConvertedDateInput!
				$cloudId: ID!
			) {
				confluence_createAutoConvertedDate(cloudId: $cloudId, input: $input) {
					success
					errors {
						extensions {
							errorType
							statusCode
						}
						message
					}
				}
			}
		`,
		{
			onError: (err) => {
				// If an error occurred, track it with an analytics event
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'error',
						actionSubject: 'markAutoConvertedDate',
						source: EDIT_PAGE_V2.name,
						attributes: {
							contentId,
							isGQLError: true,
							message: err.message,
						},
					},
				});
				markErrorAsHandled(err);
			},
			refetchQueries: () => [
				{
					// Refetch query data for whether page is marked as auto-converted
					query: UseIsAutoConvertedRecentlyQuery,
					variables: {
						contentId,
					},
				},
			],
		},
	);

	const markAutoConvertedDate = useCallback(async () => {
		// Mark the page as auto-converted by setting the autoConvertedDate content property
		const { data } = await markAutoConvertedDateMutation({
			variables: {
				cloudId,
				input: {
					contentId,
				},
			},
		});

		const errors = data?.confluence_createAutoConvertedDate?.errors;
		// If an error occurred, track it with an analytics event
		if (errors?.length) {
			errors.forEach((err) =>
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'error',
						actionSubject: 'markAutoConvertedDate',
						source: EDIT_PAGE_V2.name,
						attributes: {
							contentId,
							isGQLError: false,
							message: err.message,
						},
					},
				}).fire(),
			);
		}
	}, [cloudId, contentId, createAnalyticsEvent, markAutoConvertedDateMutation]);

	return { markAutoConvertedDate };
};
